import { type FC } from 'react';
import styles from './EmployedItem.module.scss';
import { InfoEmployeds } from 'components/InfoEmployeds';
import { getFormatPhone } from 'utils/getFotmatPhone';
import { useNavigate } from 'react-router-dom';
import { addSpaceNumber } from 'utils/addSpaceNumber';
import moment from 'moment';
import { Employed } from 'redux/Api/employedsApi/type';
import { EmployedProblem } from 'pages/EmployedsContent/utils';
import { EmployedsСategory } from '../../components/EmployedsСategory';
import { TextWithTooltip } from 'hooks/TextWithTooltip';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { TooltipDate } from 'ui/CustomerOrderItem';
import { ReactComponent as IconDone } from './img/iconDone.svg';
import { ReactComponent as IconReject } from './img/iconReject.svg';
import { ReactComponent as IconTime } from './img/iconTime.svg';
import { ReactComponent as IconCancel } from './img/iconCancel.svg';
import { ContractStatus } from 'redux/Api/contractsApi/type';
import { statusColor } from 'components/ContractsListMobile';
import { AppTooltip } from 'ui/AppTooltip';

interface EmployedItemTypes {
  employed: Employed;
  role: string;
  setIsCreatedModalOpen: (value: boolean) => void;
  isCategoryOpen?: boolean;
  onCategoryToggle?: () => void;
}

export const statusIcons: Record<ContractStatus, JSX.Element> = {
  signed_by_customer: <IconTime />,
  signed_by_worker: <IconDone />,
  rejected: <IconCancel />,
  revoked: <IconReject />,
  terminated: <IconReject />,
  expired: <IconReject />,
};

export const statusText: Record<ContractStatus, string> = {
  signed_by_customer: 'Ждем подпись',
  signed_by_worker: 'Договор подписан',
  rejected: 'Договор отклонен',
  revoked: 'Договор отозван',
  terminated: 'Договор расторгнут',
  expired: 'Истек срок',
};

export const EmployedItem: FC<EmployedItemTypes> = ({
  employed: initialEmployed,
  role,
  setIsCreatedModalOpen,
  isCategoryOpen,
  onCategoryToggle,
}) => {
  const navigate = useNavigate();
  const employedFromRedux = useSelector((state: RootState) =>
    state.employeds.items.find((e) => e.id === initialEmployed.id),
  );

  const employed = employedFromRedux || initialEmployed;

  const activeCategoriesIds = employed?.categories?.map((cat) => cat.id);

  return (
    <div
      className={role === 'director' ? styles.rootCustomer : styles.root}
      onClick={() => navigate(`/employees/${employed.id}`)}>
      {role !== 'director' && <p>{employed.id}</p>}
      <div className={styles.name} style={{ padding: '0 12px' }}>
        <InfoEmployeds
          status={employed.worker_app?.app_status}
          avatar={employed.worker_app?.avatar}
          id={employed.id}
          name={employed.name}
          surname={employed.surname}
          patronymic={employed.patronymic}
          inn={employed.inn}
          isDeleted={employed.is_deleted}
        />
      </div>
      <p style={{ padding: '0 12px' }}>{getFormatPhone(employed.phone) || ''}</p>

      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        {employed?.categories?.length > 0 && (
          <div className={styles.categoryes}>
            <TextWithTooltip content={employed.categories[0].name} />
            {employed?.categories?.length > 1 && (
              <AppTooltip
                label={
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '4px',
                    }}>
                    {employed.categories.slice(1).map((cat, idx) => (
                      <div key={idx}>{cat.name}</div>
                    ))}
                  </div>
                }
                withinPortal={false}
                openDelay={300}>
                <p>+{employed.categories.length - 1}</p>
              </AppTooltip>
            )}
          </div>
        )}
        <div className={isCategoryOpen ? styles.iconEditActive : styles.iconEdit}>
          <EmployedsСategory
            isOpen={isCategoryOpen}
            onToggle={onCategoryToggle}
            setIsCreatedModalOpen={setIsCreatedModalOpen}
            employedId={String(employed.id)}
            activeCategories={activeCategoriesIds}
            fallbackCategories={employed.categories}
          />
        </div>
      </div>

      <div style={{ padding: '0 12px' }}>
        {employed.worker_app?.moderated === 1 ? (
          <TextWithTooltip content={employed.worker_app?.nationality || ''} />
        ) : (
          ''
        )}
      </div>
      <div className={styles.contractCell}>
        <div style={{ padding: '0 12px' }}>
          <TooltipDate date={moment(employed.created_at).format('DD.MM.YYYY HH:mm')} />
        </div>
        <div className={styles.contractStatusRow}>
          <div className={styles.contractStatusIcon} style={{ padding: '0 0 0 12px' }}>
            {employed?.unified_contract ? (
              statusIcons[employed.unified_contract?.status as ContractStatus]
            ) : (
              <div className={styles.noContract}>Нет договора</div>
            )}
          </div>
          <div className={styles.statusWithDate_text}>
            <div
              className={styles.statusText}
              style={{
                color: statusColor[employed.unified_contract?.status as ContractStatus],
              }}>
              <TextWithTooltip
                content={statusText[employed.unified_contract?.status as ContractStatus]}
                className={styles.statusTextContent}
                isMobile
              />
            </div>
          </div>
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'end',
          padding: '0 12px',
        }}>
        {employed.limit ? (
          <div className={styles.limit}>
            <span>{addSpaceNumber(employed.monthly_payment)}</span>
            <p>
              {' из '}
              {addSpaceNumber(employed.limit)}
            </p>
          </div>
        ) : (
          'Не указан'
        )}
      </div>
      <div style={{ padding: '0 12px' }}>
        <EmployedProblem employed={employed} screen='desktop' />
      </div>
    </div>
  );
};
